import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './History.css';
import { ChevronDown, ChevronRight } from 'lucide-react';

const timelineEvents = [
    {
        year: '2005',
        title: 'Groundbreaking αTMS Research Publication',
        description: 'Publication of research on Therapeutic Effects of Individualized Alpha Frequency Transcranial Magnetic Stimulation (αTMS) on Schizophrenia.',
        details: 'This pivotal study from 2002 laid the foundation for individualized TMS treatments, demonstrating the effectiveness of frequency-targeted stimulation.',
        link: "//dv4tl7yyk1zlp.cloudfront.net/635d95ea-c9c3-4b0f-ad86-4d668306526e/b1b8b71b-11c2-4488-8824-66ae8e771518.pdf"
    },
    {
        year: '2009',
        title: 'NEST Technology Development',
        description: 'Launch of Neuro-EEG Synchronization Therapy (NEST) following FDA approval of TMS.',
        details: 'Dr. Jin developed his first clinical protocol through NeoSync, marking the initial commercialization of his innovative approach to neuromodulation therapy.'
    },
    {
        year: '2012',
        title: 'MeRT Technology Introduction',
        description: 'Development of Magnetic EEG-guided Resonant Therapy (MeRT).',
        details: 'Through Newport Brain Research Lab, Dr. Jin advanced the field with MeRT, which synchronizes repetitive magnetic stimulation with individual brain rhythms and organ systems. This technology was implemented globally through Brain Treatment Centers.'
    },
    {
        year: '2019',
        title: 'Neural Communication Patent',
        description: 'Patent granted for neural communication between tissue regions.',
        details: 'This patent represented a significant milestone in understanding and implementing tissue-level neural communication techniques.'
    },
    {
        year: '2020',
        title: 'EMBP™ Innovation',
        description: 'Development and patenting of Electromagnetic Brain Pulsing (EMBP™) by NoetherTech™.',
        details: 'EMBP™ marked the most significant advancement yet, introducing quantitative analysis-driven, customized pulse-matching electromagnetic stimulation for brain normalization. This non-invasive neural modulation treatment represents the culmination of years of research and development.'
    }
];

const TimelineEvent = ({ event, isExpanded, onToggle, onClick, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const eventRef = useRef(null);

  useEffect(() => {
    const currentRef = eventRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div 
      ref={eventRef}
      className={`timeline-event ${isVisible ? 'visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="year-bubble">
        <div className="year-inner" />
      </div>

      <div className="event-content">
        <div 
          className={`event-card ${isExpanded ? 'expanded' : ''}`}
          onMouseEnter={() => onToggle(true)}
          onMouseLeave={() => onToggle(false)}
          onClick={(e) => {
            e.preventDefault();
            onClick();
          }}
        >
          <div className="event-header">
            <div>
              <span className="event-year">{event.year}</span>
              <h3 className="event-title">{event.title}</h3>
              <p className="event-description">{event.description}</p>
            </div>
            {isExpanded ? 
              <ChevronDown className="chevron" /> :
              <ChevronRight className="chevron" />
            }
          </div>

          <div className={`event-details ${isExpanded ? 'expanded' : ''}`}>
            <div className="details-content">
              {event.details}
              {event.link && (
                <a 
                  href={event.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="read-more"
                  onClick={(e) => e.stopPropagation()}
                >
                  Click Here To Read
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function History() {
  const particlesRef = useRef(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);

  // Star Field Animation setup
  useEffect(() => {
    const canvas = particlesRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    let animationId;

    // Star field configuration
    const STAR_COLOR = '#89f9ff';
    const STAR_SIZE = 3;
    const STAR_MIN_SCALE = 0.2;
    const OVERFLOW_THRESHOLD = 50;
    const STAR_COUNT = (window.innerWidth + window.innerHeight) / 8;

    let scale = 1;
    let width, height;
    let stars = [];
    let pointerX, pointerY;
    let velocity = { x: 0, y: 0, tx: 0, ty: 0, z: 0.0005 };
    let touchInput = false;

    function generate() {
      for (let i = 0; i < STAR_COUNT; i++) {
        stars.push({
          x: 0,
          y: 0,
          z: STAR_MIN_SCALE + Math.random() * (1 - STAR_MIN_SCALE)
        });
      }
    }

    function placeStar(star) {
      star.x = Math.random() * width;
      star.y = Math.random() * height;
    }

    function recycleStar(star) {
      let direction = 'z';
      let vx = Math.abs(velocity.x);
      let vy = Math.abs(velocity.y);

      if (vx > 1 || vy > 1) {
        let axis;
        if (vx > vy) {
          axis = Math.random() < vx / (vx + vy) ? 'h' : 'v';
        } else {
          axis = Math.random() < vy / (vx + vy) ? 'v' : 'h';
        }

        if (axis === 'h') {
          direction = velocity.x > 0 ? 'l' : 'r';
        } else {
          direction = velocity.y > 0 ? 't' : 'b';
        }
      }

      star.z = STAR_MIN_SCALE + Math.random() * (1 - STAR_MIN_SCALE);

      if (direction === 'z') {
        star.z = 0.1;
        star.x = Math.random() * width;
        star.y = Math.random() * height;
      } else if (direction === 'l') {
        star.x = -OVERFLOW_THRESHOLD;
        star.y = height * Math.random();
      } else if (direction === 'r') {
        star.x = width + OVERFLOW_THRESHOLD;
        star.y = height * Math.random();
      } else if (direction === 't') {
        star.x = width * Math.random();
        star.y = -OVERFLOW_THRESHOLD;
      } else if (direction === 'b') {
        star.x = width * Math.random();
        star.y = height + OVERFLOW_THRESHOLD;
      }
    }

    function resize() {
      scale = window.devicePixelRatio || 1;
      width = canvas.offsetWidth * scale;
      height = canvas.offsetHeight * scale;

      canvas.width = width;
      canvas.height = height;

      stars.forEach(placeStar);
    }

    function update() {
      velocity.tx *= 0.96;
      velocity.ty *= 0.96;

      velocity.x += (velocity.tx - velocity.x) * 0.8;
      velocity.y += (velocity.ty - velocity.y) * 0.8;

      stars.forEach((star) => {
        star.x += velocity.x * star.z;
        star.y += velocity.y * star.z;

        star.x += (star.x - width / 2) * velocity.z * star.z;
        star.y += (star.y - height / 2) * velocity.z * star.z;
        star.z += velocity.z;

        if (star.x < -OVERFLOW_THRESHOLD || star.x > width + OVERFLOW_THRESHOLD || star.y < -OVERFLOW_THRESHOLD || star.y > height + OVERFLOW_THRESHOLD) {
          recycleStar(star);
        }
      });
    }

    function render() {
      context.clearRect(0, 0, width, height);

      stars.forEach((star) => {
        context.beginPath();
        context.lineCap = 'round';
        context.lineWidth = STAR_SIZE * star.z * scale;
        context.globalAlpha = 0.5 + 0.5 * Math.random();
        context.strokeStyle = STAR_COLOR;

        context.beginPath();
        context.moveTo(star.x, star.y);

        var tailX = velocity.x * 2;
        var tailY = velocity.y * 2;

        if (Math.abs(tailX) < 0.1) tailX = 0.5;
        if (Math.abs(tailY) < 0.1) tailY = 0.5;

        context.lineTo(star.x + tailX, star.y + tailY);
        context.stroke();
      });
    }

    function step() {
      update();
      render();
      animationId = requestAnimationFrame(step);
    }

    function movePointer(x, y) {
      if (typeof pointerX === 'number' && typeof pointerY === 'number') {
        let ox = x - pointerX;
        let oy = y - pointerY;

        velocity.tx = velocity.tx + (ox / 8 * scale) * (touchInput ? 1 : -1);
        velocity.ty = velocity.ty + (oy / 8 * scale) * (touchInput ? 1 : -1);
      }

      pointerX = x;
      pointerY = y;
    }

    function onMouseMove(event) {
      touchInput = false;
      const rect = canvas.getBoundingClientRect();
      movePointer(event.clientX - rect.left, event.clientY - rect.top);
    }

    function onTouchMove(event) {
      touchInput = true;
      const rect = canvas.getBoundingClientRect();
      movePointer(event.touches[0].clientX - rect.left, event.touches[0].clientY - rect.top);
      event.preventDefault();
    }

    function onMouseLeave() {
      pointerX = null;
      pointerY = null;
    }

    // Initialize
    generate();
    resize();
    step();

    // Event listeners
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('touchmove', onTouchMove);
    canvas.addEventListener('touchend', onMouseLeave);
    canvas.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('resize', resize);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onMouseLeave);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // Add intersection observer for fade-in animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-fade-in');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const toggleExpanded = (year) => {
    setExpandedIds(prev => {
      if (prev.includes(year)) {
        return prev.filter(id => id !== year);
      } else {
        return [...prev, year];
      }
    });
  };

  return (
    <div id="history-page">
      <Helmet>
        <title>Our History | NoetherTech</title>
      </Helmet>
      {/* Title Section */}
      <div id="history-title">
        <canvas ref={particlesRef} className="particles-container"></canvas>
        <div id="titleTextContainer">
          <h1 className="animate-fade-in">OUR LEGACY</h1>
        </div>
      </div>

      {/* Who We Are Section */}
      <section className="content-section">
        <div className="section-container">
          <h2 className="animate-fade-in">Who We Are</h2>
          <div className="content-text animate-fade-in delay-1">
            <p> 
              NoetherTech emerges from a history of neurological innovation. Our team combines decades of expertise in neuroscience, 
              machine learning, and medical technology to transform how neurological and psychiatric disorders are treated.
            </p>
          </div>
          <div className="content-text animate-fade-in delay-1">
            <p> 
              We are the innovators behind the Newport Brain Research Laboratory (NBRL), Brain Treatment Center (BTC), and the creators of 
              both MRT and MeRT technologies currently used by WaveNeuro™. 
            </p>
          </div>
          <div className="content-text animate-fade-in delay-1">
            <p> 
              Building on this pioneering work, we've developed EMBP™ technology to provide the next evolution in personalized neuromodulation therapy.
            </p>
          </div>
        </div>
      </section>


      {/* New Timeline Section */}
      <section className="content-section highlight">
        <div className="section-container">
          <h2 className="animate-fade-in">Our Journey to EMBP™</h2>
          <div className="timeline-wrapper">
            <div className="timeline-line" />
            {timelineEvents.map((event, index) => (
              <TimelineEvent
                key={event.year}
                event={event}
                isExpanded={expandedIds.includes(event.year) || hoveredId === event.year}
                onToggle={(shouldExpand) => {
                  if (!expandedIds.includes(event.year)) {
                    setHoveredId(shouldExpand ? event.year : null);
                  }
                }}
                onClick={() => toggleExpanded(event.year)}
                delay={index * 200}
              />
            ))}
          </div>
          
          {/* Add the history diagram */}
          <div className="history-diagram">
            {/* <h3>History of Electromagnetic Brain Pulsing Treatment</h3> */}
            <img 
              src="/historyofembp.png"
              alt="History of EMBP Treatment Timeline"
              className="history-timeline-image"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export default History;
